/**
 * Resolving simultaneous claims against a single discard — split out of
 * `Claims.ts` for the 200-line cap; re-exported from there so callers keep
 * importing from `Claims.js`.
 */

import { SEAT_COUNT, type Seat } from './types.js';
import type { ChiOption } from './Claims.js';

/** Priority rank of each claim kind — higher beats lower. */
const CLAIM_RANK = { ron: 3, pung: 2, chi: 1 } as const;

/** A declared claim, resolved against others by `resolveClaimPriority`. */
export type Claim =
  | { readonly type: 'ron'; readonly seat: Seat }
  | { readonly type: 'pung'; readonly seat: Seat }
  | { readonly type: 'chi'; readonly seat: Seat; readonly option: ChiOption };

/** Turn-order distance from `discarder` to `seat` (1..3; the discarder is 0). */
function seatDistance(seat: Seat, discarder: Seat): number {
  return (seat - discarder + SEAT_COUNT) % SEAT_COUNT;
}

/**
 * The single winning claim among simultaneous declarations, or `null` if none.
 * Ron > Pung > Chi; ties broken by the seat closest after the discarder.
 */
export function resolveClaimPriority(claims: readonly Claim[], discarder: Seat): Claim | null {
  let best: Claim | null = null;
  for (const claim of claims) {
    if (best === null) {
      best = claim;
      continue;
    }
    const rankDelta = CLAIM_RANK[claim.type] - CLAIM_RANK[best.type];
    if (rankDelta > 0) {
      best = claim;
    } else if (
      rankDelta === 0 &&
      seatDistance(claim.seat, discarder) < seatDistance(best.seat, discarder)
    ) {
      best = claim;
    }
  }
  return best;
}
